import { colors } from "@/constant/colors";
import useAudioStore from "@/store/useAudioStore";
import { Check, Pause, Play, Reply, X } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import ExpandExplanation from "./expandExplanation";
import NormalButton from "../Button/normal";

interface PracticeDictationProps {
  question: any;
  handleAnswer: (option: string, isCorrect: boolean, autoSubmit?: boolean) => void;
  currentAnswer: any;
  disabled: boolean;
  isReview?: boolean;
  onInputChange?: (value: string) => void;
  triggerCheck?: boolean;
}

const PracticeDictation = ({ question, handleAnswer, currentAnswer, disabled, isReview = false, onInputChange, triggerCheck }: PracticeDictationProps) => {
  const [userInput, setUserInput] = useState('');
  const playPollyTTS = useAudioStore(state => state.playPollyTTS);
  const isPlaying = useAudioStore(state => state.isPlaying);
  const playingType = useAudioStore(state => state.playingType);
  const setPlayingType = useAudioStore(state => state.setPlayingType);
  const interruptAudio = useAudioStore(state => state.interruptAudio);
  const [isSubmit, setIsSubmit] = useState(false);
  const [replayCount, setReplayCount] = useState(3);
  const lastPlayedQuestionRef = useRef<string>('');
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    setUserInput('');
    setIsSubmit(false);

    // 只有当题目文本真正变化时才自动播放
    if (question?.answer && question.answer !== lastPlayedQuestionRef.current) {
      lastPlayedQuestionRef.current = question.answer;

      // 自动播放题目音频
      const autoPlayAudio = async () => {
        // 先中断当前播放的音频
        interruptAudio();
        console.log('autoPlayAudio', question.answer);

        // 等待一小段时间确保中断完成
        setTimeout(async () => {
          setPlayingType('dictation');
          await playPollyTTS(question.answer, 'Joanna', true);
          setReplayCount(2); // 自动播放消耗一次，剩余2次
        }, 100);
      };

      if (!isReview) {
        autoPlayAudio();
      }
    }
  }, [question?.text]); // 只监听question.text的变化

  useEffect(() => {
    if (currentAnswer.selectedOption === null) {
      setUserInput('');
      setIsSubmit(false);
      setReplayCount(3);
    }
  }, [currentAnswer.selectedOption]);

  useEffect(() => {
    setIsCorrect(isReview ? currentAnswer.firstIsCorrect : currentAnswer.isCorrect);
  }, [isReview, currentAnswer]);

  const handlePlayAudio = async (text: string) => {
    if (replayCount <= 0 && !isReview) {
      return;
    }
    if (text && text.length > 0) {
      if (!isPlaying) {
        setPlayingType('dictation');
        await playPollyTTS(text, 'Joanna', true);
        setReplayCount(replayCount - 1);
      }
    }
  }

  const handleSubmit = () => {
    if (userInput.trim() === '' || disabled || isSubmit) {
      return;
    }
    handleAnswer(
      userInput,
      userInput.trim().toLowerCase() === question.answer.trim().toLowerCase(),
      true // autoSubmit = true
    );
    setIsSubmit(true);
  }

  // 响应外部的Check触发
  useEffect(() => {
    if (triggerCheck && !isSubmit && userInput.trim() !== '') {
      handleSubmit();
    }
  }, [triggerCheck]);

  // 输入变化时通知父组件
  const handleInputChange = (value: string) => {
    setUserInput(value);
    if (onInputChange) {
      onInputChange(value);
    }
  };

  return (
    <div className="w-full h-full">
      {
        (!isSubmit || currentAnswer.isCorrect) && (
          <>
            {!isReview && <div className="text-2xl w-full text-[#573F35] text-center emphasize-text">
              Listen carefully, and write down the word you hear
            </div>}
            <div className={`flex flex-col gap-2 ${isReview ? 'h-[100px]' : 'h-[300px]'}`}>
              <div className={`flex flex-col ${isReview ? 'py-0' : 'py-15'} items-center justify-center`}>
                <button disabled={isPlaying} className="w-18 h-18 bg-[#00A88A] rounded-full flex items-center justify-center" onClick={() => handlePlayAudio(question.answer)}>
                  {
                    isPlaying && playingType === 'dictation' ? (
                      <Pause className="w-10 h-10" color="#ffffff" fill="#ffffff" />
                    ) : (
                      <Play className="w-10 h-10" color="#ffffff" fill="#ffffff" />
                    )
                  }
                </button>
                {!isReview && <span className="text-sm text-[#573F35] mt-2 display-text">{replayCount} Replays Left</span>}
              </div>

              {!isReview && <div className="flex flex-row items-center justify-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    className={`display-text w-[200px] h-10 rounded-lg bg-white p-2 pr-8 border-2 ${currentAnswer.selectedOption !== null && !currentAnswer.isCorrect
                      ? 'border-[#FA6467]'
                      : 'border-gray-300'
                      }`}
                    value={userInput}
                    style={{
                      fontSize: '20px',
                      background: (userInput.trim() === '' || !isSubmit) ? '#F6F6F6' : userInput.trim().toLowerCase() === question.answer?.trim().toLowerCase() ? colors.correctAnswerColorLight : colors.wrongAnswerColorLight,
                    }}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={e => {
                      if (disabled) {
                        return;
                      }
                      if (
                        e.key === 'Enter' &&
                        userInput.trim() !== '' &&
                        currentAnswer.selectedOption === null
                      ) {
                        handleSubmit();
                      }
                    }}
                    disabled={isSubmit}
                    autoFocus
                  />
                  {isSubmit && userInput.trim().toLowerCase() === question.answer?.trim().toLowerCase() && (
                    <Check
                      size={20}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600"
                    />
                  )}
                </div>
              </div>}
            </div>
          </>
        )
      }
      {((isSubmit && !currentAnswer.isCorrect) || isReview) && (
        <>
          <div className="text-2xl w-full text-[#573F35] text-center emphasize-text">Correct Answer</div>
          <div className="flex flex-col items-center justify-center gap-2 h-[200px]">
            <div className="text-2xl font-bold flex flex-1 justify-center items-center text-[#573F35] display-text">{question.answer}</div>
            <div className="text-xl text-[#573F35] display-text">Your Answer</div>
            <div className="text-lg text-[#573F35] w-[300px] h-[40px] rounded-lg flex items-center justify-between display-text" style={{ backgroundColor: isCorrect ? colors.correctAnswerColorLight : colors.wrongAnswerColorLight }}>
              <div className="w-[50px]" />
              {isReview ? currentAnswer.firstSelectedOption : userInput}
              <div className="w-[50px] flex items-center justify-center">
                {
                  isCorrect ? (
                    <Check className="w-5 h-5" color={colors.correctAnswerColor} />
                  ) : (
                    <X className="w-5 h-5" color={colors.wrongAnswerColor} />
                  )
                }
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PracticeDictation;